// Canonical ticket status vocabulary — mirrors TicketStatus.EDN_STANDARD in the
// backend (app/models/persistence.py) and EDN_STATUS_MAPPING in the EDN adapter.
// `name` is EDN's own French wording (what logisticians see in their TMS) and is
// used verbatim in the EDN status pickers; `label` is the English fallback used
// through i18n. Any new status must be added here, not in a component.

export const EDN_STATUS_MAPPING = [
  { value: 'new', name: 'Nouveau', label: 'New' },
  { value: 'affected', name: 'Affecté', label: 'Assigned' },
  { value: 'incomplete', name: 'Dossier incomplet', label: 'Incomplete file' },
  { value: 'npec_transcan', name: 'NPEC Transcan', label: 'NPEC Transcan' },
  { value: 'npec_yourstore', name: 'NPEC Yourstore', label: 'NPEC Yourstore' },
  { value: 'waiting_carrier_response', name: 'Attente réponse transp.', label: 'Waiting for carrier response' },
  { value: 'waiting_carrier_commercial_response', name: 'Attente réponse commercial transp.', label: 'Waiting for carrier sales response' },
  { value: 'waiting_client_response', name: 'Réponse client attendue', label: 'Waiting for client response' },
  { value: 'return_pending', name: 'En cours de retour', label: 'Return in progress' },
  { value: 'to_compensate', name: 'A indemniser', label: 'To compensate' },
  { value: 'processing', name: 'Litige en cours de traitement', label: 'Claim being processed' },
  { value: 'compensation', name: 'Indemnisation en cours', label: 'Compensation in progress' },
  { value: 'compensation_invoice_made', name: 'Indemnisation en cours (facture faite)', label: 'Compensation in progress (invoice issued)' },
  { value: 'compensation_in_progress', name: 'Indemnisation des frais de port en cours', label: 'Shipping cost compensation in progress' },
  { value: 'compensation_made_track_insurance', name: 'Indemnisé - suivi assurance', label: 'Compensated - insurance tracking' },
  { value: 'resolved', name: 'Résolu', label: 'Resolved' },
  { value: 'resolved_accepted', name: 'Résolu - accepté', label: 'Resolved - accepted' },
  { value: 'resolved_refused', name: 'Résolu - refusé', label: 'Resolved - refused' },
  { value: 'resolved_npec', name: 'Résolu NPEC', label: 'Resolved NPEC' },
];

// Internal workflow statuses: no EDN counterpart, but tickets carry them and
// the filters must offer them.
export const WORKFLOW_STATUS_MAPPING = [
  { value: 'automated', name: 'Automatisé', label: 'Automated' },
  { value: 'action_required', name: 'Action requise', label: 'Action required' },
  { value: 'assigned', name: 'Assigné', label: 'Assigned' },
  { value: 'open', name: 'Ouvert', label: 'Open' },
  { value: 'pending', name: 'En attente', label: 'Pending' },
  { value: 'warehouse_response_received', name: 'Réponse entrepôt reçue', label: 'Warehouse response received' },
  { value: 'waiting_warehouse_response', name: 'En attente de l’entrepôt', label: 'Waiting for warehouse response' },
  { value: 'processing_error', name: 'Erreur de traitement', label: 'Processing error' },
];

// Retired statuses: never offered in a picker, only rendered when an old
// document still carries them. `ai_resolved` became `resolved` + a resolution
// actor (see RESOLUTION_ACTOR below); `closed` became `resolved` (2026-10-12).
export const LEGACY_STATUS_MAPPING = [
  { value: 'ai_resolved', name: 'Résolu par IA', label: 'AI resolved' },
  { value: 'closed', name: 'Fermé', label: 'Closed' },
];

export const ALL_STATUS_MAPPING = [...EDN_STATUS_MAPPING, ...WORKFLOW_STATUS_MAPPING];

export const EDN_STATUS_VALUES = EDN_STATUS_MAPPING.map((status) => status.value);

// EDN closes a claim under any of these four statuses.
export const RESOLVED_STATUS_VALUES = ['resolved', 'resolved_accepted', 'resolved_refused', 'resolved_npec'];

export const TERMINAL_STATUS_VALUES = [...RESOLVED_STATUS_VALUES];
export const TERMINAL_READ_STATUS_VALUES = [...TERMINAL_STATUS_VALUES, 'ai_resolved', 'closed'];

const STATUS_BY_VALUE = [...ALL_STATUS_MAPPING, ...LEGACY_STATUS_MAPPING].reduce((acc, status) => {
  acc[status.value] = status;
  return acc;
}, {});

const humanize = (value) =>
  (value || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/** Canonical business status for legacy read-compatible documents. */
export const normalizeTicketStatus = (value) =>
  (value === 'ai_resolved' || value === 'closed' ? 'resolved' : value);

/** Translated label for a status, falling back to its English wording. */
export const getStatusLabel = (t, value) => {
  const normalized = normalizeTicketStatus(value);
  return t(
    `ticketStatuses.${normalized}`,
    STATUS_BY_VALUE[normalized]?.label || humanize(normalized)
  );
};

const SEMANTIC_CLASS_BY_STATUS = {
  new: 'status-new',
  affected: 'status-open',
  incomplete: 'status-open',
  npec_transcan: 'status-open',
  npec_yourstore: 'status-open',
  waiting_carrier_response: 'status-waiting',
  waiting_carrier_commercial_response: 'status-waiting',
  waiting_client_response: 'status-waiting',
  return_pending: 'status-processing',
  to_compensate: 'status-processing',
  processing: 'status-processing',
  compensation: 'status-processing',
  compensation_invoice_made: 'status-processing',
  compensation_in_progress: 'status-processing',
  compensation_made_track_insurance: 'status-processing',
  resolved: 'status-resolved',
  resolved_accepted: 'status-resolved',
  resolved_refused: 'status-resolved',
  resolved_npec: 'status-resolved',
  closed: 'status-closed',
  automated: 'status-automated',
  ai_resolved: 'status-resolved',
  action_required: 'status-pending',
  assigned: 'status-open',
  open: 'status-open',
  pending: 'status-pending',
  warehouse_response_received: 'status-open',
  waiting_warehouse_response: 'status-open',
  processing_error: 'status-pending',
};

/** Semantic badge class (status-new, status-waiting, ...) for a status. */
export const getStatusClass = (value) =>
  SEMANTIC_CLASS_BY_STATUS[normalizeTicketStatus(value)] || '';

// Who finished a ticket. Mirrors ResolutionActor in the backend and lives in
// `ticket.resolution.by`, independently of the status: any terminal status can
// have been reached by the AI, by an agent or by the client's own system.
// Mirrors ResolutionActor in the backend (app/models/persistence.py): a value
// added there must be added here in the same change (KI-53).
export const RESOLUTION_ACTOR = {
  AI: 'ai',
  HUMAN: 'human',
  INTEGRATION: 'integration',
  SYSTEM: 'system', // housekeeping (inactivity auto-close)
};

export const RESOLUTION_ACTOR_VALUES = Object.values(RESOLUTION_ACTOR);

/** Resolution actor of a ticket, mapping pre-migration `ai_resolved` docs to AI. */
export const getResolutionActor = (ticket) => {
  if (ticket?.status === 'ai_resolved') return RESOLUTION_ACTOR.AI;
  if (!TERMINAL_STATUS_VALUES.includes(ticket?.status)) return null;
  const actor = ticket?.resolution?.by;
  return RESOLUTION_ACTOR_VALUES.includes(actor) ? actor : null;
};

/** Translated resolver wording, or null when provenance is unavailable. */
export const getResolutionActorLabel = (t, ticket) => {
  const actor = getResolutionActor(ticket);
  if (!actor) return null;
  return t(`resolutionActors.${actor}`, {
    ai: 'AI',
    human: 'Agent',
    integration: 'External system',
    system: 'System',
  }[actor] || actor);
};
